import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, ViewChild, inject, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldAppearance, MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { AppApiService } from '@src/app/core/services/app-api.service';
import {
  noLeadingSpecialCharValidator,
  notOnlySpecialCharsValidator,
  requiredTrimmedValidator,
  usOnlyCountryValidator,
  usStateCodeValidator,
  usZipCodeValidator,
} from '@src/app/mepco-webview/account/utils/address-utils';
import * as countries from 'i18n-iso-countries';
import * as countriesEn from 'i18n-iso-countries/langs/en.json';
import { api } from 'src/app/app.const';
import { ZIP_CODE_VALIDATION_PATHS } from './address.constants';

type CountryCodeFormat = '2' | '3';

interface AddressFinderResult {
  city: string;
  street: string;
  province?: string;
  state?: string;
  country: string;
  postalCode: string;
  formatted?: string;
}

@Component({
  selector: 'app-address-form',
  templateUrl: './address.component.html',
  styleUrls: ['./address.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatIconModule,
  ],
})
export class AddressFormComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  formGroup = input.required<FormGroup>();
  floatLabel = input<'auto' | 'always' | 'never'>('always');
  disabled = input(false);
  appearance = input<MatFormFieldAppearance | undefined>(undefined);
  columnSize = input<'full' | 'quarter'>('full');
  color = input<'primary' | 'accent' | 'warn'>('accent');
  layout = input<'default' | 'threeCol'>('default');
  enableAddressSearch = input(true);
  stackCountryStreet = input(false);
  countryDisplayFormat = input<CountryCodeFormat>('2');
  countryValueFormat = input<CountryCodeFormat>('2');
  allowedCountries = input<string[] | undefined>(undefined);
  zipCodeValidationApiName = input(api.systemConfiguration.name);
  zipCodeValidationPath = input(ZIP_CODE_VALIDATION_PATHS.default);
  zipCodeValidationWithoutToken = input(false);

  countries: { alpha2: string; alpha3: string; name: string }[] = [];
  filteredCountries: { alpha2: string; alpha3: string; name: string }[] = [];
  countrySearchControl = new FormControl('');
  addressResults: AddressFinderResult[] = [];

  private suppressNextStreetSearch = false;
  private hasSelectedFromResults = false;
  private lastCountryIsUs: boolean | null = null;
  private streetSearchDebounceHandle: ReturnType<typeof setTimeout> | null = null;
  private lastStreetSearchValue = '';
  private autoCompletedZipCode: string | null = null;
  private isApplyingAddressFinder = false;
  private autoCompletedState: string | null = null;

  @ViewChild('streetTrigger') streetTrigger!: MatAutocompleteTrigger;

  private appApiService = inject(AppApiService);

  ngOnInit(): void {
    this.ensureControls();
    this.initCountries();
    this.setupListeners();
    this.syncInitialState();
    this.verifyIfAddressStateChanged();
  }

  private verifyIfAddressStateChanged() {
    const formGroup = this.formGroup();
    formGroup.controls['postalCode']?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.isApplyingAddressFinder) {
        return;
      }
      this.autoCompletedZipCode = null;
      this.autoCompletedState = null;
    });

    formGroup.controls['state']?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (!this.isApplyingAddressFinder) {
        this.autoCompletedState = null;
      }
      formGroup.controls['postalCode']?.updateValueAndValidity();
    });

    formGroup.controls['street']?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.isApplyingAddressFinder) {
        return;
      }
      this.autoCompletedZipCode = null;
      this.autoCompletedState = null;
    });

    formGroup.controls['city']?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.isApplyingAddressFinder) {
        return;
      }
      this.autoCompletedZipCode = null;
      this.autoCompletedState = null;
    });
  }

  private trimControlValue(controlName: string): void {
    const name = (controlName || '').trim();
    if (!name) return;

    const control = this.formGroup().get(name);
    if (!control) return;

    const value = control.value;
    if (typeof value !== 'string') return;

    const trimmed = value.trim();
    if (trimmed === value) return;

    control.setValue(trimmed);
    control.updateValueAndValidity();
    this.formGroup().updateValueAndValidity();
  }

  public trimField(controlName: string): void {
    this.trimControlValue(controlName);
  }

  private setupListeners(): void {
    const formGroup = this.formGroup();
    const countryControl = formGroup.get('country');
    const streetControl = formGroup.get('street');

    countryControl?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((val: string | null) => {
      if (this.isApplyingAddressFinder) {
        return;
      }
      const alpha2 = this.toAlpha2CountryCode(val);
      const normalizedValue = this.toConfiguredCountryCode(alpha2, this.countryValueFormat());
      const currentValue = (val ?? '').toString().trim().toUpperCase();

      if (val && normalizedValue && normalizedValue !== currentValue) {
        countryControl.setValue(normalizedValue, { emitEvent: false });
      }

      if (alpha2) {
        this.syncCountrySearchControl(alpha2);
      }

      const isUsa = alpha2 === 'US';

      // Clear only the opposite field when crossing US <-> non-US.
      if (this.lastCountryIsUs !== null && this.lastCountryIsUs !== isUsa) {
        if (isUsa) {
          formGroup.get('province')?.setValue('');
        } else {
          formGroup.get('state')?.setValue('');
        }
      }

      this.lastCountryIsUs = isUsa;
      this.updateAddressValidators(alpha2 || val);
    });

    this.countrySearchControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: string | null) => {
        const search = (typeof value === 'string' ? value : '').toLowerCase();
        this.filteredCountries = this.countries.filter(
          c =>
            c.name.toLowerCase().includes(search) ||
            c.alpha2.toLowerCase().includes(search) ||
            c.alpha3.toLowerCase().includes(search)
        );
      });

    if (this.enableAddressSearch() && streetControl) {
      streetControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((raw: string | null) => {
        if (this.suppressNextStreetSearch) {
          this.suppressNextStreetSearch = false;
          return;
        }

        if (this.streetSearchDebounceHandle) {
          clearTimeout(this.streetSearchDebounceHandle);
        }

        const nextValue = (raw || '').toString();
        this.streetSearchDebounceHandle = setTimeout(() => {
          if (nextValue === this.lastStreetSearchValue) {
            return;
          }
          this.lastStreetSearchValue = nextValue;

          this.hasSelectedFromResults = false;
          const val = nextValue.trim();
          if (val.length >= 3 && !streetControl.invalid) {
            this.searchAddress();
          } else {
            this.addressResults = [];
            this.streetTrigger?.closePanel();
          }
        }, 600);
      });
    }
  }

  private syncInitialState(): void {
    const formGroup = this.formGroup();
    const isCurrentlyDisabled = this.disabled() || formGroup.disabled;
    if (isCurrentlyDisabled) {
      this.countrySearchControl.disable({ emitEvent: false });
    }

    const countryControl = formGroup.get('country');
    const currentCountry = countryControl?.value;
    if (currentCountry) {
      const alpha2 = this.toAlpha2CountryCode(currentCountry);
      const normalizedValue = this.toConfiguredCountryCode(alpha2, this.countryValueFormat());
      const currentValue = currentCountry.toString().trim().toUpperCase();

      if (normalizedValue && normalizedValue !== currentValue) {
        countryControl?.setValue(normalizedValue, { emitEvent: false });
      }
      this.lastCountryIsUs = alpha2 === 'US';
      this.updateAddressValidators(alpha2 || currentCountry);
      if (alpha2) this.syncCountrySearchControl(alpha2);
    }
  }

  private toAlpha2CountryCode(value: unknown): string {
    const raw = (value ?? '').toString().trim();
    if (!raw) return '';

    const country = raw.toUpperCase();
    return countries.toAlpha2(country) || country;
  }

  private toConfiguredCountryCode(alpha2: string, format: CountryCodeFormat): string {
    if (!alpha2) return '';
    if (format === '2') return alpha2;
    return countries.alpha2ToAlpha3(alpha2) || alpha2;
  }

  onCountryBlur(): void {
    const typed = (this.countrySearchControl.value || '').toString().trim();
    const countryControl = this.formGroup().get('country');
    this.countrySearchControl.markAsTouched();
    countryControl?.markAsTouched();

    if (!typed) {
      this.countrySearchControl.setErrors({ required: true });
      countryControl?.setValue('');
      countryControl?.markAsDirty();
      countryControl?.updateValueAndValidity();
      return;
    }

    const selected = this.countries.find(c => {
      const displayCode = this.toConfiguredCountryCode(c.alpha2, this.countryDisplayFormat());
      return `${c.name} (${displayCode})`.toLowerCase() === typed.toLowerCase();
    });
    if (selected) {
      this.countrySearchControl.setErrors(null);
      const controlValue = this.toConfiguredCountryCode(selected.alpha2, this.countryValueFormat());
      if (countryControl?.value !== controlValue) {
        countryControl?.setValue(controlValue);
      }
      this.syncCountrySearchControl(selected.alpha2);
      return;
    }

    this.countrySearchControl.setErrors({ invalidCountrySelection: true });
    countryControl?.setValue('');
    countryControl?.markAsDirty();
    countryControl?.updateValueAndValidity();
  }

  private sanitizeAddressValue(value: string): string {
    const raw = (value ?? '').toString();
    if (!raw) return '';

    const noDiacritics = raw.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
    return noDiacritics
      .replace(/[^A-Za-z0-9 ]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  searchAddress(fromIconClick = false): void {
    const formGroup = this.formGroup();
    if (this.disabled() || formGroup.disabled || !this.enableAddressSearch()) return;

    const streetControl = formGroup.get('street');
    const countryControl = formGroup.get('country');
    const streetValue = (streetControl?.value || '').toString().trim();
    const countryValue = this.toAlpha2CountryCode(countryControl?.value);
    if (streetValue.length < 3) return;

    this.appApiService
      .post(api.account.name, 'address-finder', { body: { country: countryValue, street: streetValue } }, false)
      .then((resp: AddressFinderResult | AddressFinderResult[]) => {
        const results = Array.isArray(resp) ? resp : resp ? [resp] : [];
        this.addressResults = results;

        if (results.length && this.streetTrigger && (fromIconClick || !this.hasSelectedFromResults)) {
          this.streetTrigger.openPanel();
        } else {
          this.streetTrigger?.closePanel();
        }
      })
      .catch(() => {
        this.addressResults = [];
        this.streetTrigger?.closePanel();
      })
      .finally(() => formGroup.updateValueAndValidity({ emitEvent: false }));
  }

  selectAddress(result: AddressFinderResult): void {
    if (!result) return;
    this.isApplyingAddressFinder = true;

    const alpha2Country = this.toAlpha2CountryCode(result.country);
    const controlCountry = this.toConfiguredCountryCode(alpha2Country, this.countryValueFormat());
    const isUSA = alpha2Country === 'US';

    this.autoCompletedZipCode = result.postalCode;
    this.autoCompletedState = result.state || '';

    const formGroup = this.formGroup();
    const patch = (ctrl: string, val: string | null | undefined) => formGroup.get(ctrl)?.setValue(val || '');

    patch('city', this.sanitizeAddressValue(result.city));
    patch('country', controlCountry);
    patch('postalCode', result.postalCode);
    this.suppressNextStreetSearch = true;
    patch('street', this.sanitizeAddressValue(result.street));

    isUSA
      ? patch('state', result.state)
      : patch('province', this.sanitizeAddressValue(result.province || result.state || ''));

    this.syncCountrySearchControl(alpha2Country);

    formGroup.controls['postalCode']?.updateValueAndValidity();

    this.addressResults = [];
    this.isApplyingAddressFinder = false;
  }
  private initCountries(): void {
    try {
      const localeData = (countriesEn as { default?: unknown }).default ?? countriesEn;
      countries.registerLocale(localeData as never);
      const names = countries.getNames('en', { select: 'official' }) as Record<string, string>;
      this.countries = Object.keys(names)
        .map(alpha2 => ({ alpha2, alpha3: countries.alpha2ToAlpha3(alpha2) || alpha2, name: names[alpha2] }))
        .sort((a, b) => a.name.localeCompare(b.name));

      const allowedCountries = this.allowedCountries();
      if (allowedCountries?.length) {
        this.countries = this.countries.filter(c => allowedCountries.includes(c.alpha2));
      }

      this.filteredCountries = this.countries;

      const countryControl = this.formGroup().get('country');
      const rawCode = countryControl?.value || 'US';
      const alpha2 = this.toAlpha2CountryCode(rawCode) || 'US';
      const controlValue = this.toConfiguredCountryCode(alpha2, this.countryValueFormat());
      const currentValue = (countryControl?.value ?? '').toString().trim().toUpperCase();
      if (!countryControl?.value || currentValue !== controlValue) countryControl?.setValue(controlValue);

      this.syncCountrySearchControl(alpha2);
    } catch (error) {
      console.log(error);
      this.countries = [];
    }
  }

  private updateAddressValidators(country: string | null | undefined): void {
    const normalizedCountry = this.toAlpha2CountryCode(country);
    const isUSA = normalizedCountry === 'US';
    const formGroup = this.formGroup();
    const allowedCountries = this.allowedCountries();
    const controls = {
      street: formGroup.get('street'),
      country: formGroup.get('country'),
      city: formGroup.get('city'),
      state: formGroup.get('state'),
      province: formGroup.get('province'),
      postalCode: formGroup.get('postalCode'),
    };

    controls.street?.setValidators([
      requiredTrimmedValidator,
      Validators.maxLength(100),
      notOnlySpecialCharsValidator,
      noLeadingSpecialCharValidator,
    ]);
    controls.city?.setValidators([
      requiredTrimmedValidator,
      Validators.maxLength(30),
      notOnlySpecialCharsValidator,
      noLeadingSpecialCharValidator,
    ]);
    controls.country?.setValidators([requiredTrimmedValidator, Validators.maxLength(3)]);

    if (allowedCountries?.length === 1 && allowedCountries[0] === 'US') {
      controls.country?.setValidators([requiredTrimmedValidator, Validators.maxLength(3), usOnlyCountryValidator]);
    }

    if (isUSA) {
      controls.state?.setValidators([
        requiredTrimmedValidator,
        Validators.minLength(2),
        Validators.maxLength(2),
        usStateCodeValidator,
      ]);
      // Province is not used/displayed for US addresses.
      controls.province?.setValidators([]);
      controls.postalCode?.setValidators([requiredTrimmedValidator]);
      controls.postalCode?.setAsyncValidators([
        usZipCodeValidator(
          this.appApiService,
          () => this.autoCompletedZipCode,
          () => this.autoCompletedState,
          {
            apiName: this.zipCodeValidationApiName(),
            path: this.zipCodeValidationPath(),
            withoutToken: this.zipCodeValidationWithoutToken(),
          }
        ),
      ]);
      controls.postalCode?.updateValueAndValidity({
        emitEvent: false,
      });
    } else {
      // All non-US countries (including Canada) — province and postal code are optional.
      controls.state?.setValidators([]);
      controls.province?.setValidators([Validators.maxLength(30), notOnlySpecialCharsValidator]);
      controls.postalCode?.clearAsyncValidators();
      controls.postalCode?.setValidators([Validators.maxLength(20), notOnlySpecialCharsValidator]);
      controls.postalCode?.updateValueAndValidity();
    }

    Object.values(controls).forEach(c => c?.updateValueAndValidity({ emitEvent: false }));
    formGroup.updateValueAndValidity({ emitEvent: false });
  }

  private syncCountrySearchControl(alpha2: string): void {
    const selected = this.countries.find(c => c.alpha2 === alpha2);
    if (selected) {
      const displayCode = this.toConfiguredCountryCode(selected.alpha2, this.countryDisplayFormat());
      this.countrySearchControl.setValue(`${selected.name} (${displayCode})`, { emitEvent: false });
    }
  }

  private ensureControls(): void {
    const fg = this.formGroup();
    const config: Record<string, ValidatorFn[]> = {
      street: [
        requiredTrimmedValidator,
        Validators.maxLength(100),
        notOnlySpecialCharsValidator,
        noLeadingSpecialCharValidator,
      ],
      country: [requiredTrimmedValidator, Validators.maxLength(3)],
      city: [
        requiredTrimmedValidator,
        Validators.maxLength(30),
        notOnlySpecialCharsValidator,
        noLeadingSpecialCharValidator,
      ],
      postalCode: [requiredTrimmedValidator],
      state: [Validators.maxLength(2)],
      province: [Validators.maxLength(30), notOnlySpecialCharsValidator],
    };
    Object.keys(config).forEach(key => {
      if (!fg.get(key)) fg.addControl(key, new FormControl('', config[key]));
    });
  }

  onCountrySelected(country: { alpha2: string; alpha3: string; name: string }): void {
    this.formGroup().get('country')?.setValue(this.toConfiguredCountryCode(country.alpha2, this.countryValueFormat()));
    this.countrySearchControl.setErrors(null);
    this.syncCountrySearchControl(country.alpha2);
    this.addressResults = [];
  }

  clearCountrySearch(event: MouseEvent): void {
    event.stopPropagation();
    this.countrySearchControl.setValue('');
    this.countrySearchControl.markAsTouched();
    this.countrySearchControl.markAsDirty();
    this.countrySearchControl.setErrors({ required: true });
    const countryControl = this.formGroup().get('country');
    countryControl?.setValue('');
    countryControl?.markAsTouched();
    countryControl?.markAsDirty();
    countryControl?.updateValueAndValidity();
    this.filteredCountries = this.countries;
    this.addressResults = [];
    this.streetTrigger?.closePanel();
  }

  get isUsaCountry(): boolean {
    return this.toAlpha2CountryCode(this.formGroup().get('country')?.value) === 'US';
  }

  get isCanadaCountry(): boolean {
    return this.toAlpha2CountryCode(this.formGroup().get('country')?.value) === 'CA';
  }

  get isPostalCodeRequired(): boolean {
    return this.isUsaCountry;
  }

  get isSingleCountry(): boolean {
    return this.allowedCountries()?.length === 1;
  }

  shouldShowPostalCodeError(): boolean {
    const postalCodeControl = this.formGroup().controls.postalCode;

    if (!postalCodeControl?.errors) {
      return false;
    }

    return (
      postalCodeControl.hasError('stateMismatch') ||
      postalCodeControl.hasError('zipCodeNotFound') ||
      postalCodeControl.hasError('invalidUsZipCode') ||
      postalCodeControl.hasError('invalidUsZipCodeFormat') ||
      postalCodeControl.dirty ||
      postalCodeControl.touched
    );
  }

  getOnlySpecialCharsMessage(controlName: string): string {
    const normalizedControl = (controlName || '').trim();
    const fieldName = normalizedControl ? normalizedControl.toLowerCase() : 'field';
    return `Enter valid ${fieldName} with letters and numbers. It can't be only symbols`;
  }
}
